import React, { useState, useEffect, useRef, useCallback } from "react";
import { differenceInMilliseconds, add } from "date-fns";

type CountdownProps = {
  disableDonation: boolean;
  startAt: string;
  endAt: string;
  endDateBuffer: number;
};

const CountdownTimer = ({
  startAt,
  endAt,
  endDateBuffer,
  disableDonation,
}: CountdownProps) => {
  const [isPastEvent, setIsPastEvent] = useState(true);
  const [description, setDescription] = useState("");
  const [countdown, setCountdown] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Store interval ID in ref to ensure cleanup works correctly
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize the countdown logic
  const startCounter = useCallback(() => {
    // Clear any existing interval before starting a new one
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const finalDate = disableDonation
      ? new Date(startAt)
      : add(new Date(endAt), { days: endDateBuffer });

    intervalRef.current = setInterval(() => {
      const currentTime = new Date();
      const timeDiff = differenceInMilliseconds(finalDate, currentTime);

      if (timeDiff <= 0) {
        setIsPastEvent(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        setIsPastEvent(false);
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

        setCountdown({ days, hours, minutes, seconds });
      }
    }, 1000);
  }, [disableDonation, startAt, endAt, endDateBuffer]);

  useEffect(() => {
    // Set description based on disableDonation
    setDescription(
      disableDonation ? "Fundraising Begins In" : "Fundraising Ends In"
    );

    // Start the countdown
    startCounter();

    // Cleanup: clear interval on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [disableDonation, startCounter]);

  return (
    <div>
      {!isPastEvent && (
        <div
          style={{
            color: "#333333",
            textAlign: "center",
          }}
        >
          <div
            style={{
              textTransform: "uppercase",
              display: "flex",
              flexDirection: "row",
              padding: "0 40px",
              textAlign: "center",
              justifyContent: "center",
            }}
            className="countDown-header"
          >
            <span
              style={{
                width: "25px",
                borderTop: "2px solid #cecece",
                margin: "12px 5px 0 5px",
              }}
            ></span>
            <p style={{ fontSize: "10px" }}>{description}</p>
            <span
              style={{
                width: "25px",
                borderTop: "2px solid #cecece",
                margin: "12px 5px 0 5px",
              }}
            ></span>
          </div>
          <div
            style={{
              justifyContent: "center",
              display: "flex",
              fontSize: "16px",
            }}
            className="clock"
          >
            <div className="days fwb">{countdown.days}</div>
            <span style={{ fontSize: "16px" }}>d</span>
            <span
              style={{
                fontWeight: "700",
                marginRight: "5px",
                marginLeft: "5px",
              }}
              className="semicolon"
            >
              {" "}
              :{" "}
            </span>
            <div className="hours fwb">
              {countdown.hours.toString().padStart(2, "0")}
            </div>
            <span style={{ fontSize: "16px" }}>h</span>
            <span
              style={{
                fontWeight: "700",
                marginRight: "5px",
                marginLeft: "5px",
              }}
              className="semicolon"
            >
              {" "}
              :{" "}
            </span>
            <div className="minutes fwb">
              {countdown.minutes.toString().padStart(2, "0")}
            </div>
            <span style={{ fontSize: "16px" }}>m</span>
            <span
              style={{
                fontWeight: "700",
                marginRight: "5px",
                marginLeft: "5px",
              }}
              className="semicolon"
            >
              {" "}
              :{" "}
            </span>
            <div className="seconds fwb">
              {countdown.seconds.toString().padStart(2, "0")}
            </div>
            <span style={{ fontSize: "16px" }}>s</span>
            <span
              style={{
                fontWeight: "700",
                marginRight: "5px",
                marginLeft: "5px",
              }}
              className="semicolon"
            ></span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountdownTimer;
